import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, FileSignature, FilePen, History } from "lucide-react";

const LEVELS = ["First written", "Second written", "Final written"];
const SHORT = [["date", "Date", "date"], ["time", "Time", "time"], ["site", "Site / location"], ["agency", "Baiada or agency"], ["supervisor_name", "Supervisor"], ["manager_name", "Manager"]];
const LONG = [["reason", "Reason for issuing"], ["detail", "What happened"], ["corrective", "Corrective actions"]];
const PAPER = [["witness", "Employee's witness"], ["employee_response", "Employee response"]];

function History_({ wc }) {
  const h = wc.history || [];
  return (
    <div className="rounded-xl border border-orange-200 bg-orange-50/60 p-2.5 text-xs space-y-1" data-testid="cf-warn-history">
      <div className="font-semibold text-orange-800 inline-flex items-center gap-1"><History size={12} /> {h.length ? `${h.length} warning(s) already on file` : "No warnings on file yet"} · suggested level: <b data-testid="cf-warn-suggested">{wc.suggested}</b></div>
      {h.map((w) => <div key={w.id} className="text-slate-600">{w.level} · {w.date} · {w.reason}{w.paper ? " (paper)" : ""}</div>)}
    </div>
  );
}

function ModeToggle({ paper, onPick, id }) {
  const b = (on) => `flex-1 h-10 rounded-xl text-xs font-semibold inline-flex items-center justify-center gap-1.5 border transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`;
  return (
    <div className="flex gap-2">
      <button data-testid={`cf-warn-mode-paper-${id}`} onClick={() => onPick("paper")} className={b(paper)}><FileSignature size={14} /> Signed paper form: log it</button>
      <button data-testid={`cf-warn-mode-draft-${id}`} onClick={() => onPick("draft")} className={b(!paper)}><FilePen size={14} /> New warning: draft it</button>
    </div>
  );
}

export function WarningPanel({ c, f, setF, save }) {
  const wc = c.proposal?.warning_check;
  if (!wc) return null;
  const paper = c.inputs.warning_mode === "paper";
  const put = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const blur = () => save({ fields: f });
  const field = ([k, l, t]) => (
    <div key={k}><label className="text-[11px] text-slate-400">{l}</label>
      <Input data-testid={`cf-warn-${k}-${c.id}`} type={t || "text"} value={f[k] || ""} onChange={put(k)} onBlur={blur} className="h-8 text-xs mt-0.5" /></div>
  );
  return (
    <div className="space-y-2.5" data-testid={`cf-warning-${c.id}`}>
      <div className="text-xs font-semibold text-slate-500 inline-flex items-center gap-1"><AlertTriangle size={13} className="text-orange-500" /> Written warning</div>
      <ModeToggle paper={paper} id={c.id} onPick={(m) => save({ warning_mode: m })} />
      <History_ wc={wc} />
      <div className="flex items-center gap-2">
        <label className="text-[11px] text-slate-400 w-16">Level</label>
        <select data-testid={`cf-warn-level-${c.id}`} value={wc.level} onChange={(e) => { const n = { ...f, level: e.target.value }; setF(n); save({ fields: n }); }} className="h-8 flex-1 rounded-md border px-2 text-xs">
          {LEVELS.map((l) => <option key={l}>{l}</option>)}</select>
        {wc.level !== wc.suggested && <button data-testid={`cf-warn-use-suggested-${c.id}`} onClick={() => { const n = { ...f, level: wc.suggested }; setF(n); save({ fields: n }); }} className="text-[11px] font-semibold text-orange-700 underline">Use {wc.suggested}</button>}
      </div>
      <div className="grid grid-cols-2 gap-2">{SHORT.map(field)}</div>
      {LONG.map(([k, l]) => (
        <div key={k}><label className="text-[11px] text-slate-400">{l}</label>
          <Textarea data-testid={`cf-warn-${k}-${c.id}`} value={f[k] || ""} onChange={put(k)} onBlur={blur} rows={k === "reason" ? 1 : 2} className="text-xs mt-0.5 min-h-0" /></div>))}
      {paper && <>
        <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" data-testid={`cf-warn-signed-${c.id}`} className="w-4 h-4 accent-indigo-600" checked={["true", "yes", "1"].includes(String(f.employee_signed).toLowerCase())}
          onChange={(e) => { const n = { ...f, employee_signed: e.target.checked }; setF(n); save({ fields: n }); }} /> The employee signed the paper form</label>
        <div className="grid grid-cols-2 gap-2">{PAPER.map(field)}</div>
      </>}
    </div>
  );
}
